<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import { isCompanyAdminFromToken } from "../../utils/authRole";
import { useCompanyKyc, kycStatusFlag } from "../../composables/useCompanyKyc";

const route = useRoute();
const { isBusiness, status } = useCompanyKyc();

const isAdmin = computed(() => isCompanyAdminFromToken(localStorage.getItem("_panda")));
const flag = computed(() => kycStatusFlag(status.value));

const on = (prefix: string) => route.path === prefix || route.path.startsWith(prefix + "/");
</script>

<template>
  <nav class="ac-nav" aria-label="Account">
    <div class="ac-nav-section">
      <div class="ac-nav-label">You</div>
      <router-link to="/account/profile" class="ac-nav-item" :class="{ 'is-active': on('/account/profile') }">
        <span class="ac-nav-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
        </span>
        My profile
      </router-link>
      <router-link to="/account/password" class="ac-nav-item" :class="{ 'is-active': on('/account/password') }">
        <span class="ac-nav-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
        </span>
        Change password
      </router-link>
    </div>

    <!-- Company and order settings are for admins; members see only their
         own profile and password (router ADMIN_ONLY_PATH_PREFIXES). -->
    <div v-if="isAdmin" class="ac-nav-section">
      <div class="ac-nav-label">Company</div>
      <router-link v-if="isAdmin" to="/account/business" class="ac-nav-item" :class="{ 'is-active': on('/account/business') }">
        <span class="ac-nav-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
        </span>
        Business profile
      </router-link>
      <router-link v-if="isAdmin" to="/account/team" class="ac-nav-item" :class="{ 'is-active': on('/account/team') }">
        <span class="ac-nav-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
        </span>
        Team
      </router-link>
      <router-link v-if="isBusiness" to="/account/verification" class="ac-nav-item" :class="{ 'is-active': on('/account/verification') }">
        <span class="ac-nav-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>
        </span>
        Verification
        <span v-if="flag" class="ac-nav-flag" :class="`ac-pill--${flag.tone}`">{{ flag.short }}</span>
      </router-link>
    </div>

    <div v-if="isAdmin" class="ac-nav-section">
      <div class="ac-nav-label">Settings</div>
      <router-link to="/account/settings/orders" class="ac-nav-item" :class="{ 'is-active': on('/account/settings/orders') }">
        <span class="ac-nav-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></svg>
        </span>
        Order settings
      </router-link>
      <router-link to="/account/settings/otp" class="ac-nav-item" :class="{ 'is-active': on('/account/settings/otp') }">
        <span class="ac-nav-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></svg>
        </span>
        OTP settings
      </router-link>
    </div>
  </nav>
</template>
